import { useContext, useEffect, useRef, useState } from 'react';
import { AuthorizedImageContext } from './AuthorizedImageProvider';
import type { AuthorizedImageLoadPriority } from './AuthorizedImage.types';

type AuthorizedImageStatus = 'idle' | 'loading' | 'loaded' | 'error';

interface UseAuthorizedImageOptions {
  priority?: AuthorizedImageLoadPriority;
}

export interface UseAuthorizedImageResult {
  src: string | null;
  status: AuthorizedImageStatus;
  error: Error | null;
}

/**
 * Hook do pobierania chronionego obrazu z nagłówkiem `Authorization`.
 * Wynik (obiektowy URL) jest cache'owany w `AuthorizedImageProvider`,
 * więc równoległe wywołania dla tego samego `imageUrl` współdzielą jeden fetch.
 */
export const useAuthorizedImage = (
  imageUrl: string | undefined,
  options?: UseAuthorizedImageOptions,
): UseAuthorizedImageResult => {
  const ctx = useContext(AuthorizedImageContext);
  const priority = options?.priority ?? 'normal';
  const priorityRef = useRef(priority);
  priorityRef.current = priority;
  const previousLoadStateRef = useRef<{
    imageUrl: string | undefined;
    priority: AuthorizedImageLoadPriority;
  }>({
    imageUrl: undefined,
    priority,
  });
  const [src, setSrc] = useState<string | null>(null);
  const [error, setError] = useState<Error | null>(null);
  const [status, setStatus] = useState<AuthorizedImageStatus>(
    imageUrl ? 'loading' : 'idle',
  );

  useEffect(() => {
    if (!imageUrl) {
      setSrc(null);
      setError(null);
      setStatus('idle');
      return;
    }
    if (!ctx) {
      const err = new Error(
        'useAuthorizedImage: brak AuthorizedImageProvider w drzewie komponentów.',
      );
      setError(err);
      setStatus('error');
      return;
    }

    let cancelled = false;
    const abortController = new AbortController();
    setStatus('loading');
    setError(null);

    ctx
      .load(imageUrl, {
        priority: priorityRef.current,
        signal: abortController.signal,
      })
      .then((objectUrl) => {
        if (cancelled) return;
        setSrc(objectUrl);
        setStatus('loaded');
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setError(err instanceof Error ? err : new Error(String(err)));
        setStatus('error');
      });

    return () => {
      cancelled = true;
      abortController.abort();
    };
  }, [imageUrl, ctx]);

  useEffect(() => {
    const previousLoadState = previousLoadStateRef.current;
    previousLoadStateRef.current = { imageUrl, priority };

    if (
      !ctx ||
      !imageUrl ||
      priority !== 'high' ||
      previousLoadState.imageUrl !== imageUrl ||
      previousLoadState.priority === 'high'
    ) {
      return;
    }

    const abortController = new AbortController();

    ctx
      .load(imageUrl, {
        priority: 'high',
        signal: abortController.signal,
      })
      .catch(() => undefined);

    return () => {
      abortController.abort();
    };
  }, [imageUrl, ctx, priority]);

  return { src, status, error };
};
